import { useSelector } from 'react-redux';

import { PlanType, ALL_PLANS } from 'constants/plan-type';
import { getSubscriptionPlanName } from 'helpers/subscription-plans';
import { useLicenseProperties } from 'hooks/connectivity/configuration-api/properties/use-license-properties';
import { LimitsProperty, PlatformNamespace } from 'services/connectivity/configuration-api/properties/constants';
import { getCanManageSubscription, getPlanType } from 'store/features/session/selectors';

import { CALENDLY_URL_FOR_VISITOR_LIMIT_EXCEEDED, getVisitorLimitExceededMessagePrefix } from '../constants';

interface UseVisitorLimitExceededDataResult {
  messagePrefix: string;
  ctaLabel: string;
  externalCtaUrl: string | null;
}

export function useVisitorLimitExceededData(): UseVisitorLimitExceededDataResult {
  const canManageSubscription = useSelector(getCanManageSubscription);
  const planType = useSelector(getPlanType);
  const { data: limitsProperties } = useLicenseProperties(PlatformNamespace.LIMITS);
  const limit = calculateVisitorLimit(
    limitsProperties?.[LimitsProperty.MonitoredCustomersBase] as number | undefined,
    limitsProperties?.[LimitsProperty.MonitoredCustomersAdditional] as number | undefined,
  );
  const messagePrefix = getVisitorLimitExceededMessagePrefix(limit);

  const { label: ctaLabel, externalUrl } = getCtaConfig(canManageSubscription, planType);

  return {
    messagePrefix,
    ctaLabel,
    externalCtaUrl: externalUrl,
  };
}

function getCtaConfig(
  canManageSubscription: boolean,
  planType: PlanType | null,
): { label: string; externalUrl: string | null } {
  if (!canManageSubscription) {
    return { label: 'Ask to upgrade', externalUrl: null };
  }

  const isEnterpriseOrBusinessPlan = planType === PlanType.Business || planType === PlanType.EnterprisePlus;

  if (isEnterpriseOrBusinessPlan) {
    return { label: 'Book call', externalUrl: CALENDLY_URL_FOR_VISITOR_LIMIT_EXCEEDED };
  }

  if (planType && ALL_PLANS.includes(planType)) {
    const currentPlanIndex = ALL_PLANS.indexOf(planType);
    const nextPlan = currentPlanIndex < ALL_PLANS.length - 1 ? ALL_PLANS[currentPlanIndex + 1] : null;

    if (nextPlan) {
      const nextPlanName = getSubscriptionPlanName(nextPlan);

      return { label: `Upgrade to ${nextPlanName}`, externalUrl: null };
    }
  }

  return { label: 'Upgrade', externalUrl: null };
}

function calculateVisitorLimit(base: number | undefined, additional: number | undefined): number | null {
  if (base === undefined && additional === undefined) {
    return null;
  }

  return (base ?? 0) + (additional ?? 0);
}
